import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { formatPln, parsePln, plnToInput } from '../catalog/money';
import { todayInWarsaw } from '../events/calendar';
import { ConflictNotice } from '../ui/ConflictNotice';
import { FORM_NOTE_LABEL, FORMS, KINDS, type EventPayments, type Form, type Installment, type InstallmentKind, type Method } from './types';
import { sameValues, useOpening, useUnsavedGuard } from '../ui/unsaved';

type Row = { key: string; id?: string; kind: InstallmentKind; label: string; amount: string; dueDate: string; form: Form; formNote: string };
let seq = 0;
/** An older server sends only the method: transfer was invoiced. */
const FORM_OF_METHOD: Record<Method, Form> = { przelew: 'faktura', gotowka: 'gotowka', barter: 'barter' };
const rowOf = (i: Installment): Row => ({ key: i.id, id: i.id, kind: i.kind, label: i.label, amount: i.amountGrosze !== undefined ? plnToInput(i.amountGrosze) : '', dueDate: i.dueDate, form: i.form ?? FORM_OF_METHOD[i.method], formNote: i.formNote ?? '' });

/**
 * "Zmień plan" (F36): the installments of the event's plan in place — name, amount, due date, settlement form (owner
 * 2026-10-22: with "Podmiot" of an invoice by another entity, the contract of "umowa o dzieło"); add or remove one. The server checks the sum against the accepted quote's gross and says the expected sum when it differs.
 */
/** `newForm`: the form of an added installment — an event „bez faktury” (owner 2026-11-03) gives its own, else „Faktura”. */
export function PlanEditor({ eventId, plan, onClose, onSaved, newForm = 'faktura' }: { eventId: string; plan: NonNullable<EventPayments['plan']>; onClose: () => void; onSaved: () => void; newForm?: Form }) {
  const [rows, setRows] = useState<Row[]>(plan.installments.map(rowOf));
  const [local, setLocal] = useState('');
  const opening = useOpening(rows);
  const save = useMutation({
    mutationFn: (installments: unknown[]) => api(`/api/payments/events/${eventId}/plan`, { method: 'PUT', body: { installments, version: plan.version } }),
    onSuccess: onSaved,
  });
  const set = (key: string, patch: Partial<Row>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  function sumOf(list: Row[]): number | null {
    try {
      return list.reduce((s, r) => s + parsePln(r.amount), 0);
    } catch {
      return null;
    }
  }
  /** What to send, or null with the reason shown. */
  function payload() {
    const fail = (why: string) => (setLocal(why), null);
    if (rows.length === 0) return fail('Plan musi mieć co najmniej jedną ratę');
    const installments = [];
    for (const r of rows) {
      let amountGrosze: number;
      try {
        amountGrosze = parsePln(r.amount);
      } catch (error) {
        return fail(`${r.label || 'Rata'}: ${error instanceof Error ? error.message : 'nieprawidłowa kwota'}`);
      }
      if (amountGrosze <= 0) return fail(`${r.label || 'Rata'}: kwota musi być większa od zera`);
      if (!r.label.trim()) return fail('Każda rata musi mieć nazwę');
      if (!/^\d{4}-\d{2}-\d{2}$/.test(r.dueDate)) return fail(`${r.label}: podaj termin`);
      if (r.form === 'faktura_inna' && !r.formNote.trim()) return fail(`${r.label}: podaj podmiot, który wystawia fakturę`);
      installments.push({ ...(r.id ? { id: r.id } : {}), kind: r.kind, label: r.label.trim(), amountGrosze, dueDate: r.dueDate, form: r.form, formNote: FORM_NOTE_LABEL[r.form] ? r.formNote.trim() : '' });
    }
    setLocal('');
    return installments;
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    const sent = payload();
    if (sent !== null) save.mutate(sent);
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({
    dirty: !sameValues(rows, opening),
    save: async () => {
      const sent = payload();
      if (sent === null) return false;
      await save.mutateAsync(sent);
      return true;
    },
  });
  const apiError = save.error instanceof ApiError ? save.error : null;
  const expected = apiError?.code === 'plan_sum' ? (apiError.details as { expectedGrosze?: number } | undefined)?.expectedGrosze : undefined;
  const error = apiError && apiError.status !== 409 ? (fieldIssues(apiError)[0]?.message ?? apiError.message) : save.error && !apiError ? 'Nie udało się zapisać' : '';
  const sum = sumOf(rows);
  return (
    <form ref={guardRef} className="pay-plan-editor" aria-label="Plan płatności – edycja" onSubmit={submit} noValidate data-feature-id="payments.plan">
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Rata</th>
              <th scope="col">Rodzaj</th>
              <th scope="col" className="num">
                Kwota (zł)
              </th>
              <th scope="col">Termin</th>
              <th scope="col">Forma rozliczenia</th>
              <th scope="col">
                <span className="visually-hidden">Działania</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td>
                  <input aria-label="Nazwa raty" value={r.label} maxLength={100} onChange={(e) => set(r.key, { label: e.target.value })} />
                </td>
                <td>
                  <select aria-label="Rodzaj raty" value={r.kind} onChange={(e) => set(r.key, { kind: e.target.value as InstallmentKind })}>
                    {(Object.keys(KINDS) as InstallmentKind[]).map((k) => (
                      <option key={k} value={k}>
                        {KINDS[k]}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="num">
                  <input
                    aria-label="Kwota raty (zł)"
                    className="num"
                    inputMode="decimal"
                    value={r.amount}
                    onChange={(e) => set(r.key, { amount: e.target.value })}
                    onBlur={() => {
                      try {
                        set(r.key, { amount: plnToInput(parsePln(r.amount)) });
                      } catch {
                        /* the message comes on saving */
                      }
                    }}
                  />
                </td>
                <td>
                  <input aria-label="Termin raty" type="date" value={r.dueDate} onChange={(e) => set(r.key, { dueDate: e.target.value })} />
                </td>
                <td className="pay-form-cell">
                  <select aria-label="Forma rozliczenia raty" value={r.form} onChange={(e) => set(r.key, { form: e.target.value as Form })}>
                    {(Object.keys(FORMS) as Form[]).map((f) => (
                      <option key={f} value={f}>
                        {FORMS[f]}
                      </option>
                    ))}
                  </select>
                  {FORM_NOTE_LABEL[r.form] && <input aria-label={FORM_NOTE_LABEL[r.form]} placeholder={FORM_NOTE_LABEL[r.form]} value={r.formNote} maxLength={200} onChange={(e) => set(r.key, { formNote: e.target.value })} />}
                </td>
                <td className="q-actions">
                  <button className="btn icon ghost" type="button" aria-label={`Usuń ratę: ${r.label}`} title="Usuń" onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}>
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="pay-plan-sum">
        <button className="btn ghost small" type="button" onClick={() => setRows((rs) => [...rs, { key: `new-${(seq += 1)}`, kind: 'inna', label: '', amount: '', dueDate: todayInWarsaw(), form: newForm, formNote: '' }])}>
          + Dodaj ratę
        </button>
        <span className="muted">Suma: {sum === null ? '—' : formatPln(sum)}</span>
      </p>
      {(local || error) && (
        <p className="alert" role="alert">
          {local || error}
          {expected !== undefined && ` Powinna wynosić ${formatPln(expected)}.`}
        </p>
      )}
      {apiError?.status === 409 && <ConflictNotice onReload={onSaved} />}
      <div className="actions">
        <button className="btn primary small" type="submit" disabled={save.isPending}>
          Zapisz plan
        </button>
        <button className="btn small" type="button" onClick={onClose}>
          Anuluj
        </button>
      </div>
    </form>
  );
}
